import Button from '@/common/components/Button'
import Modal from '@/common/components/Modal'
import TextArea from '@/common/components/TextArea'
import { ErrorResponse } from '@/common/types/errorResponse'
import { handleServerError } from '@/common/utils/handleServerError'
import { PaymentModalityType } from '@/features/companies/types/companies.type'
import { useReviewCompanyGovernmentAllocationEntry } from '@/features/privateCompanies/hooks/useReviewCompanyGovernmentAllocationEntry'
import {
  declineSchema,
  GovernmentAllocationDeclineValidationSchema,
} from '@/features/privateCompanies/pages/CompanyGovernmentAllocations/schemas/decline.schema'
import { zodResolver } from '@hookform/resolvers/zod'
import { useIsMutating, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'

interface DeclineGovernmentAllocationEntryModalProps {
  governmentAllocationKind?: PaymentModalityType
}

export const DeclineGovernmentAllocationEntryModal = ({
  governmentAllocationKind,
}: DeclineGovernmentAllocationEntryModalProps) => {
  const { id } = useParams()
  const queryClient = useQueryClient()

  const isMutating = !!useIsMutating()

  const [isOpen, setIsOpen] = useState(false)
  const [baseError, setBaseError] = useState('')

  const {
    register,
    handleSubmit,
    reset: resetForm,
    setError,
    formState: { errors },
  } = useForm<GovernmentAllocationDeclineValidationSchema>({
    resolver: zodResolver(declineSchema),
  })

  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['companyGovernmentAllocationEntry', id] })
    resetForm()
    setIsOpen(false)
  }

  const onError = (error: ErrorResponse) => {
    handleServerError({ error, setBaseError, setError })
  }

  const { mutate: declineGovernmentAllocationEntry, isPending: declineGovernmentAllocationEntryIsPending } =
    useReviewCompanyGovernmentAllocationEntry({
      governmentAllocationId: id,
      state: 'disapproved',
      onSuccess,
      onError,
    })

  const onSubmit: SubmitHandler<GovernmentAllocationDeclineValidationSchema> = (data) => {
    setBaseError('')
    declineGovernmentAllocationEntry(data)
  }

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      onConfirm={handleSubmit(onSubmit)}
      Trigger={
        <Button type="button" variant="yellow" className="w-[164px]" disabled={isMutating}>
          Declinar
        </Button>
      }
      useCancellableFooter
      confirmButtonClassName="bg-blue-700"
      disabled={declineGovernmentAllocationEntryIsPending}
      title="Solicitando revisão"
      titleClass="text-2xl font-bold mb-2"
      subtitle={
        governmentAllocationKind === 'pre_paid'
          ? 'Esse empenho será devolvido para revisão.'
          : 'Essa cota será devolvida para revisão.'
      }
      body={
        <form className="flex flex-col gap-4">
          {baseError && <p className="text-sm text-danger-soft">{baseError}</p>}
          <TextArea
            label="Justificativa"
            errorMessage={errors?.description?.message}
            {...register('description')}
            markAsRequired
          />
        </form>
      }
    />
  )
}
